import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { FadeUp, Stagger, StaggerItem } from "@/components/Reveal";
import { useLanguage } from "@/i18n/LanguageContext";
import Seo from "@/components/Seo";

/* ------------------------------------------------------------------ */
/*  À propos, humain, minimaliste, phrases courtes.                  */
/* ------------------------------------------------------------------ */

export default function Maison() {
 const { locale } = useLanguage();
 const l = (fr, en, pt) => (locale === "en" ? en : locale === "pt" ? pt : fr);

 const values = [
 { t: l("Le travail bien fait", "Work done properly", "O trabalho bem feito"), b: l("Le détail compte, même quand personne ne regarde.", "The detail matters, even when no one is watching.", "O detalhe conta, mesmo quando ninguém está a olhar.") },
 { t: l("D'ici, sans folklore", "From here, without folklore", "Daqui, sem folclore"), b: l("Nous venons de la région, nous n'en faisons pas une carte postale.", "We come from the region, we don't turn it into a postcard.", "Somos da região, não a transformamos num postal.") },
 { t: l("La curiosité", "Curiosity", "A curiosidade"), b: l("Chercher ce qui marche vraiment, pas seulement ce qui plaît.", "Looking for what really works, not just what pleases.", "Procurar o que realmente funciona, não apenas o que agrada.") },
 { t: l("Le compte tenu", "Accountability", "A responsabilidade"), b: l("Envers les gens, envers ce qu'on prélève, envers ce qu'on laisse.", "To the people, to what we take, to what we leave behind.", "Para com as pessoas, para com o que se retira, para com o que se deixa.") },
 { t: l("Passer le relais", "Passing the baton", "Passar o testemunho"), b: l("Ce que nous apprenons doit pouvoir servir à d'autres, après nous.", "What we learn must be able to serve others, after us.", "O que aprendemos deve poder servir a outros, depois de nós.") },
 ];

 const links = [
 { to: "/impact", label: l("Impact", "Impact", "Impacto"), desc: l("Producteurs, emplois, projets, nos résultats concrets.", "Producers, jobs, projects, our concrete results.", "Produtores, empregos, projectos, os nossos resultados concretos.") },
 { to: "/partenaires", label: l("Partenaires", "Partners", "Parceiros"), desc: l("Coopératives, laboratoires, institutions.", "Cooperatives, laboratories, institutions.", "Cooperativas, laboratórios, instituições.") },
 { to: "/rejoindre", label: l("Nous rejoindre", "Join us", "Junte-se a nós"), desc: l("Distributeurs, investisseurs, talents.", "Distributors, investors, talents.", "Distribuidores, investidores, talentos.") },
 ];

 return (
 <div data-testid="page-maison" className="bg-white">
 <Seo path="/maison" />

 {/* Hero, clair, phrase courte */}
 <section className="border-b border-nanda-line">
 <div className="mx-auto max-w-7xl px-6 sm:px-12 pt-36 md:pt-48 pb-20 md:pb-28">
 <FadeUp>
 <div className="label-overline text-nanda-muted mb-8">{l("À propos", "About", "Sobre")}</div>
 </FadeUp>
 <FadeUp delay={0.1}>
 <h1 className="font-sans font-light tracking-tight text-nanda-ink text-5xl sm:text-6xl md:text-7xl leading-[1.02] text-balance max-w-4xl">
 {l("Une marque. Plusieurs savoir-faire.", "One brand. Several crafts.", "Uma marca. Vários saberes.")}
 </h1>
 </FadeUp>
 <FadeUp delay={0.2}>
 <p className="mt-10 max-w-2xl text-nanda-inkSoft text-lg md:text-xl font-light leading-relaxed">
 {l(
 "NANDA SÁ est une entreprise fondée en 2023 en Guinée-Bissau. Elle réunit quatre activités : l'énergie solaire, les cosmétiques naturels, les produits du terroir et la formation.",
 "NANDA SÁ is a company founded in 2023 in Guinea-Bissau. It brings together four activities: solar energy, natural cosmetics, terroir products and training.",
 "A NANDA SÁ é uma empresa fundada em 2023 na Guiné-Bissau. Reúne quatro actividades: a energia solar, os cosméticos naturais, os produtos do terroir e a formação."
 )}
 </p>
 </FadeUp>
 </div>
 </section>

 {/* Manifeste, phrases courtes */}
 <section className="border-b border-nanda-line">
 <div className="mx-auto max-w-4xl px-6 sm:px-12 py-24 md:py-36">
 <FadeUp>
 <div className="label-overline text-nanda-muted mb-10">{l("Le Manifeste", "The Manifesto", "O Manifesto")}</div>
 </FadeUp>
 <FadeUp delay={0.1} className="space-y-8">
 {[
 l("NANDA SÁ est une entreprise bissau-guinéenne, fondée en 2023.", "NANDA SÁ is a Bissau-Guinean company, founded in 2023.", "A NANDA SÁ é uma empresa bissau-guineense, fundada em 2023."),
 l("Nous installons le solaire.", "We install solar.", "Instalamos o solar."),
 l("Nous fabriquons des cosmétiques naturels.", "We make natural cosmetics.", "Fabricamos cosméticos naturais."),
 l("Nous vendons du sel et du miel d'ici.", "We sell salt and honey from here.", "Vendemos sal e mel daqui."),
 l("Et nous transmettons ce que nous savons faire.", "And we pass on what we know how to do.", "E transmitimos o que sabemos fazer."),
 ].map((s, i) => (
 <p key={i} className={`font-sans font-light tracking-tight leading-[1.15] text-balance ${i === 0 ? "text-nanda-muted text-xl md:text-2xl" : "text-nanda-ink text-3xl md:text-5xl"}`} data-testid={`manifesto-line-${i}`}>
 {s}
 </p>
 ))}
 </FadeUp>
 <FadeUp delay={0.25} className="mt-14">
 <p className="text-nanda-inkSoft text-base md:text-lg font-light leading-relaxed max-w-2xl">
 {l(
 "Nous travaillons d'abord pour l'Afrique de l'Ouest, avec les mêmes exigences de qualité que pour l'export. Sans folklore, sans raccourcis.",
 "We work first for West Africa, with the same quality standards as for export. Without folklore, without shortcuts.",
 "Trabalhamos primeiro para a África Ocidental, com as mesmas exigências de qualidade que para a exportação. Sem folclore, sem atalhos."
 )}
 </p>
 </FadeUp>
 </div>
 </section>

 {/* Pourquoi ici */}
 <section className="border-b border-nanda-line bg-nanda-bgAlt">
 <div className="mx-auto max-w-7xl px-6 sm:px-12 py-24 md:py-32 grid grid-cols-1 md:grid-cols-12 gap-12 items-center">
 <FadeUp className="md:col-span-5">
 <div className="overflow-hidden rounded-3xl">
 <img
 src="/photos/home-hero.jpg"
 alt={l("NANDA SÁ, Guinée-Bissau", "NANDA SÁ, Guinea-Bissau", "NANDA SÁ, Guiné-Bissau")}
 className="w-full aspect-[4/5] md:aspect-[4/4] object-cover"
 loading="lazy"
 data-testid="maison-histoire-image"
 />
 </div>
 </FadeUp>
 <FadeUp delay={0.15} className="md:col-span-7 md:pl-6">
 <div className="label-overline text-nanda-muted mb-6">{l("Pourquoi ici", "Why here", "Porquê aqui")}</div>
 <h2 className="font-sans font-light tracking-tight text-4xl md:text-5xl leading-[1.1] text-nanda-ink text-balance">
 {l("La Guinée-Bissau, le Sénégal. Notre point de départ, notre horizon.", "Guinea-Bissau, Senegal. Our starting point, our horizon.", "A Guiné-Bissau, o Senegal. O nosso ponto de partida, o nosso horizonte.")}
 </h2>
 <div className="mt-8 space-y-5 text-nanda-inkSoft text-base md:text-lg font-light leading-relaxed max-w-xl">
 <p>{l(
 "Nous travaillons avec les producteurs, les artisans, les techniciens et les jeunes du Sénégal et de la Guinée-Bissau. Nous transformons sur place et nous vendons localement comme à l'export.",
 "We work with the producers, artisans, technicians and young people of Senegal and Guinea-Bissau. We process locally and we sell both locally and for export.",
 "Trabalhamos com os produtores, os artesãos, os técnicos e os jovens do Senegal e da Guiné-Bissau. Transformamos localmente e vendemos tanto no mercado local como para a exportação."
 )}</p>
 </div>
 </FadeUp>
 </div>
 </section>

 {/* Valeurs */}
 <section className="border-b border-nanda-line">
 <div className="mx-auto max-w-7xl px-6 sm:px-12 py-24 md:py-32">
 <FadeUp className="mb-14">
 <div className="label-overline text-nanda-muted mb-4">{l("Ce qui nous tient", "What holds us", "O que nos mantém")}</div>
 <h2 className="font-sans font-light tracking-tight text-4xl md:text-5xl text-nanda-ink">{l("Cinq valeurs, une exigence.", "Five values, one standard.", "Cinco valores, uma exigência.")}</h2>
 </FadeUp>
 <Stagger className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6">
 {values.map((v, i) => (
 <StaggerItem key={i} className="border border-black/[0.06] rounded-3xl bg-white p-8 hover:border-black/20 hover:shadow-[0_20px_40px_rgba(0,0,0,0.04)] transition-all duration-500" data-testid={`maison-value-${i}`}>
 <div className="label-overline text-nanda-muted">0{i + 1}</div>
 <div className="font-sans font-medium tracking-tight text-xl md:text-2xl mt-5 text-nanda-ink leading-tight">{v.t}</div>
 <p className="mt-4 text-nanda-inkSoft text-sm font-light leading-relaxed">{v.b}</p>
 </StaggerItem>
 ))}
 </Stagger>
 </div>
 </section>

 {/* Liens */}
 <section>
 <div className="mx-auto max-w-7xl px-6 sm:px-12 py-24 md:py-32 grid grid-cols-1 md:grid-cols-3 gap-6">
 {links.map((c) => (
 <Link key={c.to} to={c.to} className="group border border-black/[0.06] rounded-3xl bg-white p-10 hover:border-black/20 hover:shadow-[0_20px_40px_rgba(0,0,0,0.04)] transition-all duration-500" data-testid={`maison-link-${c.to.slice(1)}`}>
 <div className="label-overline text-nanda-muted">{c.label}</div>
 <p className="mt-5 font-sans font-light tracking-tight text-2xl leading-snug text-nanda-ink">{c.desc}</p>
 <span className="mt-8 inline-flex items-center gap-2 text-sm font-medium text-nanda-ink group-hover:translate-x-1 transition-transform duration-500">
 {l("Découvrir", "Discover", "Descobrir")} <ArrowUpRight size={14} strokeWidth={1.5} />
 </span>
 </Link>
 ))}
 </div>
 </section>
 </div>
 );
}
